import type { RefObject, SyntheticEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getScrollBehavior } from "../utils";

export enum ScrollMethod {
    Half = "half",
    Full = "full",
    Item = "item",
}

type UseScrollSnapProps<T extends HTMLElement> = {
    sliderRef: RefObject<T>;
    itemCount: number;
    /**
     * Either a number or a ScrollMethod.
     * ScrollMethods are dynamically calculated based on the visibleSlidesIndices (= items that are currently visible).
     * Numbers are static and scroll by that amount.
     */
    scrollAmount: number | ScrollMethod;
    /**
     * The threshold at which the intersection observer should trigger.
     * Think of this as the percentage of the element that should be visible before the observer triggers.
     * Defaults to 0 (trigger when the first pixel of the element becomes visible).
     */
    intersectionThreshold?: number;
    disabled?: boolean;
    interactionTrack?: () => void;
    urlTrack?: () => void;
    disableDesktopDragging?: boolean;
};

type UseScrollSnapReturnType = {
    isDragging: boolean;
    handleChange: (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>, dir: "left" | "right") => void;
    handleMouseDrag: (evt: MouseEvent) => void;
    sliderIndex: number;
    addNode: (node: HTMLElement | null, index: number) => void;
    onClick: (evt: SyntheticEvent) => void;
    visibleNodeCount: number | null;
    reachedEnd: boolean;
    reachedStart: boolean;
    hasItemsToLeft: boolean;
    hasItemsToRight: boolean;
    isScrolling: boolean;
    observerInitialized: boolean;
};

export const useScrollSnap = <T extends HTMLElement>(
    scrollSnapProps: UseScrollSnapProps<T>,
): UseScrollSnapReturnType => {
    const { sliderRef, interactionTrack, urlTrack } = scrollSnapProps;
    const { disableDesktopDragging, itemCount, scrollAmount, disabled, intersectionThreshold = 0 } = scrollSnapProps;

    const [isDragging, setIsDragging] = useState(false);
    const [sliderIndex, setSliderIndex] = useState(0);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [reachedEnd, setReachedEnd] = useState(false);
    const [reachedStart, setReachedStart] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [scrollAmountNumber, setScrollAmountNumber] = useState<number>(1);
    const [observerInitialized, setObserverInitialized] = useState<boolean>(false);

    const observer = useRef<IntersectionObserver | null>(null);
    const lastVisibleSlideIndex = useRef(0);
    const visibleSlidesIndices = useRef<number[]>([]);
    const slideRefs = useRef<T[]>([]);

    /**
     * Callback that is fired whenever the observers detects a change in the visibility of the element.
     * This callback is used to determine the current index of the slider.
     */
    const intersectionCallback = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                const index = slideRefs.current.indexOf(entry.target as T);
                // If the element is visible, add it to the list of visible slides.
                if (entry.isIntersecting) {
                    lastVisibleSlideIndex.current = index;
                    visibleSlidesIndices.current.push(index);
                } else {
                    // If the element is not visible, remove it from the list of visible slides.
                    visibleSlidesIndices.current = visibleSlidesIndices.current.filter((elem = 0) => elem !== index);
                }

                // Determine the current index of the slider based on the list of visible slides.
                const currentIndex = Math.min(...visibleSlidesIndices.current);
                setSliderIndex(currentIndex);

                // Set the scroll amount based on the scrollAmount prop
                // We place this here to ensure that the scrollAmount is recalculated whenever the visibleSlidesIndices change.
                switch (scrollAmount) {
                    case ScrollMethod.Half:
                        setScrollAmountNumber(Math.ceil(visibleSlidesIndices.current.length / 2));
                        break;
                    case ScrollMethod.Full:
                        setScrollAmountNumber(visibleSlidesIndices.current.length);
                        break;
                    case ScrollMethod.Item:
                        setScrollAmountNumber(1);
                        break;
                    default:
                        setScrollAmountNumber(typeof scrollAmount === "number" ? scrollAmount : 1);
                }
            }, []);
        },
        [scrollAmount],
    );

    // Add a node to be observed
    // Will re-observe the nodes whenever a new node is added
    const addNode = useCallback((node: T | null, index: number) => {
        if (!node) return;

        // Add the node to the list of slideRefs
        slideRefs.current[index] = node;

        // Re-observe the nodes
        if (observer.current) observer.current.observe(node);
    }, []);

    // Returns the width of the first item in the slider
    const getItemWidth = (): number => (sliderRef.current?.firstChild?.firstChild as HTMLElement)?.clientWidth || 0;

    // Scrolls the slider left or right, using the width of the first item in the slider
    const handleChange = useCallback(
        (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>, dir: "left" | "right"): void => {
            if (!sliderRef.current) return;

            evt.stopPropagation();
            evt.preventDefault();
            interactionTrack?.();

            const behavior = getScrollBehavior();
            const scrollDirection = dir === "left" ? -1 : 1;

            try {
                sliderRef.current.scrollTo({
                    left: sliderRef.current.scrollLeft + getItemWidth() * scrollAmountNumber * scrollDirection,
                    behavior,
                });
            } catch (ignored) {
                // On the screens the scrollAmountNumber is always 0, so it is harcoded to 4
                sliderRef.current.scrollLeft += getItemWidth() * 4 * scrollDirection;
            }
            setIsScrolling(true);
        },
        [scrollAmountNumber, sliderRef.current],
    );

    // Handles mouse dragging of the slider
    const handleMouseDrag = useCallback(
        (evt: MouseEvent): void => {
            if (!sliderRef.current || !isDragging || disableDesktopDragging) return;
            evt.stopPropagation();
            evt.preventDefault();

            const mouseX = evt.clientX - sliderRef.current.offsetLeft;
            const scrollAmountX = (mouseX - startX) * 2;
            sliderRef.current.scrollLeft = scrollLeft - scrollAmountX;
        },
        [disableDesktopDragging, isDragging, scrollLeft, sliderRef, startX],
    );

    /**
     * Handles the mouse getting clicked (and held) on the slider.
     * This is used to determine the starting x-coordinate of the mouse relative to the slider.
     */
    const handleMouseDown = useCallback(
        (evt: MouseEvent): void => {
            if (!sliderRef.current) return;

            setIsDragging(true);
            setStartX(evt.clientX - sliderRef.current.offsetLeft);
            setScrollLeft(sliderRef.current.scrollLeft);
        },
        [sliderRef],
    );

    const handleMouseUp = useCallback((): void => {
        setIsDragging(false);
        setStartX(0);
        setScrollLeft(0);
    }, []);

    const handleMouseLeave = useCallback((): void => {
        setIsDragging(false);
    }, []);

    const onClick = (evt: SyntheticEvent): void => {
        // Prevent redirection when the slider is still being dragged.
        if (isDragging) evt.preventDefault();
        else urlTrack?.();
    };

    useEffect(() => {
        if (disabled || !sliderRef.current) return undefined;

        if (observer.current) observer.current.disconnect();

        // Create a new observer
        observer.current = new IntersectionObserver(intersectionCallback, {
            root: sliderRef.current,
            threshold: intersectionThreshold,
        });

        // Attach an observer to each slide of the slider
        slideRefs.current.forEach((node) => {
            if (node && observer.current) {
                // Observe every node in the slider
                observer.current.observe(node);
            }
        });

        if (!observerInitialized) {
            setObserverInitialized(true);
        }

        return () => observer.current?.disconnect();
    }, [disabled, intersectionCallback, observer, sliderRef, intersectionThreshold, itemCount, observerInitialized]);

    /**
     * Attach eventListeners for all functions with their relevant event types.
     * When the component dismounts, remove the eventListeners.
     */
    useEffect(() => {
        const currentSliderRef = sliderRef.current;

        if (!currentSliderRef || disabled) return;

        currentSliderRef.addEventListener("mousedown", handleMouseDown);
        currentSliderRef.addEventListener("mouseup", handleMouseUp);
        currentSliderRef.addEventListener("mousemove", handleMouseDrag);
        currentSliderRef.addEventListener("mouseleave", handleMouseLeave);

        // eslint-disable-next-line consistent-return
        return () => {
            if (!currentSliderRef) return;

            currentSliderRef.removeEventListener("mousedown", handleMouseDown);
            currentSliderRef.removeEventListener("mouseup", handleMouseUp);
            currentSliderRef.removeEventListener("mousemove", handleMouseDrag);
            currentSliderRef.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [handleMouseDown, handleMouseUp, handleMouseDrag, handleMouseLeave, disabled, sliderRef, scrollAmountNumber]);

    /**
     * Set isScrolling to false after the scroll event has ended.
     */
    useEffect(() => {
        const currentSliderRef = sliderRef.current;

        const handleScrollEnd = (): void => {
            setIsScrolling(false);
        };

        currentSliderRef?.addEventListener("scrollend", handleScrollEnd);

        return () => {
            currentSliderRef?.removeEventListener("scrollend", handleScrollEnd);
        };
    }, [sliderRef]);

    /**
     * Set reachedEnd and reachedStart based on the current slider index.
     */
    useEffect(() => {
        setReachedStart(sliderIndex === 0);
        setReachedEnd(lastVisibleSlideIndex.current === itemCount - 1);
    }, [itemCount, sliderIndex]);

    return {
        isDragging,
        handleChange,
        handleMouseDrag,
        sliderIndex,
        addNode: addNode as (node: HTMLElement | null, index: number) => void,
        visibleNodeCount: visibleSlidesIndices.current.length,
        onClick,
        reachedEnd,
        reachedStart,
        hasItemsToLeft: sliderIndex > 0,
        hasItemsToRight: sliderIndex < itemCount - scrollAmountNumber,
        isScrolling,
        observerInitialized,
    };
};
